import { ApplicationRef, EmbeddedViewRef, EnvironmentInjector, Injectable, createComponent, inject } from "@angular/core";

import { Subject } from "rxjs";

import { Utils } from "@utils";

import { ModalComponent } from "./modal.component";
import { ModalAnimationState } from "./modal.enums";
import { ModalConfig, ModalInstance, ModalWithData } from "./modal.interfaces";

@Injectable({ providedIn: "root" })
export class ModalService {
  private readonly aR = inject(ApplicationRef);
  private readonly inj = inject(EnvironmentInjector);

  private instances: ModalInstance[] = [];

  private readonly clickFn = (event: MouseEvent): void => {
    const instance = this.instances[this.instances.length - 1];
    if (instance && instance.config.outside) {
      const target = event.target as HTMLElement;
      if (!instance.componentRef.instance.container().nativeElement.contains(target)) {
        this.close(instance);
      }
    }
  };

  public open<T>(config: ModalConfig<T>): ModalInstance<T> | null {
    if (!config) {
      return null;
    }

    const componentRef = createComponent(ModalComponent, { environmentInjector: this.inj });
    const modalInstance = componentRef.instance;
    config.styleClass && componentRef.location.nativeElement.classList.add(config.styleClass);
    modalInstance.container().nativeElement.classList.add(ModalAnimationState.V);

    const contentRef = modalInstance.content().createComponent(config.component, { injector: componentRef.injector });
    const contentInstance = contentRef.instance as ModalWithData<any>;
    if (config.data) {
      contentInstance.data = config.data;
    }
    modalInstance.content().insert(contentRef.hostView);

    this.aR.attachView(componentRef.hostView);

    const domElem = (componentRef.hostView as EmbeddedViewRef<any>).rootNodes[0] as HTMLElement;
    document.body.appendChild(domElem);

    componentRef.changeDetectorRef.detectChanges();

    const instance: ModalInstance<T> = {
      componentRef,
      config,
      contentRef,
      close$: new Subject<boolean>(),
    };

    contentInstance.instance = instance;

    this.instances.push(instance);

    return instance;
  }

  public close<T>(instance: ModalInstance<T>, apply: boolean = false): void {
    if (!instance) {
      return;
    }

    instance.close$.next(apply);
    instance.componentRef.instance.container().nativeElement.classList.add(ModalAnimationState.H);

    setTimeout(() => {
      this.instances = this.instances.filter(it => it !== instance);
      instance.componentRef.hostView && this.aR.detachView(instance.componentRef.hostView);
      instance.contentRef.destroy();
      instance.componentRef.destroy();
    }, Utils.duration);
  }

  public closeAll(): void {
    this.instances.forEach(instance => this.close(instance));
  }

  public init(): void {
    document.addEventListener("click", this.clickFn);
  }

  public destroy(): void {
    document.removeEventListener("click", this.clickFn);
  }
}
